import React, { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { IonCol, IonItem, IonLabel, IonRow, IonSegment, IonSegmentButton, IonIcon, IonNavLink } from '@ionic/react'
import { useDispatch, useSelector } from 'react-redux'
import { closeCircle } from 'ionicons/icons'
import NumberPad from '../../../components/numberPad/NumberPad.component'
import Validation from '../../../components/validation/Validation.component'
import { IPHIComponent } from '../../../components/phi/phi.component'
import { IRootState } from '../../../store'
import { saveVitals, setGlucose, setGlucosePNNV } from '../store/vitals.actions'
import { VitalsConstants } from '../store/vitals.constants'
import './Glucose.component.css'
import { IDataPointsConfig } from '../../configurations/store/configurations.types'
import { getDatapointsConfigSelector } from '../../configurations/selectors'
import { getAllPNNVOptionsSelector } from '../../options/selectors'
import { IPNNVOption } from '../../../common/common.types'
import { numberPadKeysWithDecimal } from '../../../components/numberPad/NumberPad.types'

const GlucoseSelectionComponent: React.FC<IPHIComponent> = () => {
  const [focus, isFocused] = useState(false)
  const vitals = useSelector((state: IRootState) => state.vitals)
  const { glucose, key, glucosePNNV } = vitals
  const datapointsConfig: IDataPointsConfig = useSelector(getDatapointsConfigSelector)
  const { DP_VS_GLU } = datapointsConfig
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const allPNNVOptions = useSelector(getAllPNNVOptionsSelector)
  const glucosePNNVOptions = allPNNVOptions?.['eVitals.18']

  const glucoseLevel =
    glucose === t('vitals.advanced.form.Glucose_High') || glucose === t('vitals.advanced.form.Glucose_Low')
  const glucoseLevelOptions = ['Low', 'High']

  const numberPadRef: any = useRef(null)

  const setGlucoseOnChange = (value: string[] | null | string) => {
    const glucose = typeof value !== 'string' ? (value?.length ? value.join('') : null) : value
    dispatch(setGlucose(glucose))
    if (glucosePNNV) dispatch(setGlucosePNNV(null))
    dispatch(saveVitals())
  }

  const setGlucosePNNVOnChange = (option: IPNNVOption | null) => {
    dispatch(setGlucosePNNV(option))
    if (glucose) dispatch(setGlucose(''))
    dispatch(saveVitals())
  }

  return (
    <>
      {DP_VS_GLU === 1 || glucose || focus ? (
        <IonRow>
          <IonCol sizeXs='5' className='picklist'>
            <IonItem lines='none' style={{ display: glucoseLevel ? 'block' : 'none' }}>
              <IonLabel color='medium'>{t('vitals.advanced.form.Glucose')}</IonLabel>
            </IonItem>
            {!glucoseLevel ? (
              <>
                <NumberPad
                  ref={numberPadRef}
                  elementId='Glucose'
                  dataTestId='Glucose'
                  label={t('vitals.advanced.form.Glucose')}
                  values={glucose ? glucose.split('') : []}
                  onNumPadClick={setGlucoseOnChange}
                  action={VitalsConstants.SET_GLUCOSE}
                  validCharacters={numberPadKeysWithDecimal}
                  onFocus={() => isFocused(true)}
                  onBlur={() => isFocused(false)}
                  disabled={glucoseLevel}
                  pnnvAction={setGlucosePNNVOnChange}
                  pnnvListHeader={t('vitals.advanced.form.Glucose')}
                  pnnvOptions={glucosePNNVOptions}
                  pnnvValue={glucosePNNV}
                />
                <Validation id={VitalsConstants.SET_GLUCOSE} cardinalityKey={key} />
              </>
            ) : (
              <IonItem lines='none' className='selected-glucose'>
                <IonLabel color={'primary-alt'}>{glucose}</IonLabel>
              </IonItem>
            )}
          </IonCol>
          {!glucoseLevel && !glucose && !focus && (
            <IonCol className='range-selection justify-content-end'>
              <IonItem lines='none'>
                <IonLabel className='grid-item-divider-label glucose-label' color='medium'>
                  <IonNavLink
                    className='use-range-button'
                    color='primary'
                    onClick={(e) => {
                      numberPadRef?.current?.showNumberPad(e)
                    }}>
                    {' '}
                    Enter value{' '}
                  </IonNavLink>{' '}
                  or
                  <IonNavLink
                    className='use-range-button'
                    color='primary'
                    onClick={() => {
                      setGlucoseOnChange(t('vitals.advanced.form.Glucose_Low'))
                    }}>
                    Use range
                  </IonNavLink>
                </IonLabel>
              </IonItem>
            </IonCol>
          )}
          {glucoseLevel && (
            <IonCol className='mt-2 '>
              <IonSegment data-testid='GlucoseLevel' value={glucose} className='glucose-segment'>
                {glucoseLevelOptions.map((type) => (
                  <IonSegmentButton
                    key={type}
                    onClick={() => setGlucoseOnChange(t(`vitals.advanced.form.Glucose_${type}`))}
                    className='glucose-level'
                    data-testid={`GlucoseLevel${type}`}
                    value={t(`vitals.advanced.form.Glucose_${type}`)}>
                    {t(`vitals.advanced.form.Glucose_${type}`)}
                  </IonSegmentButton>
                ))}
              </IonSegment>
            </IonCol>
          )}
          {glucoseLevel && (
            <IonCol size='1' className='delete-icon-container mt-2 '>
              <IonIcon className='delete-icon' icon={closeCircle} onClick={() => setGlucoseOnChange(null)} />
            </IonCol>
          )}
        </IonRow>
      ) : null}
    </>
  )
}

export default GlucoseSelectionComponent
